<template>
  <component
    :is="tag"
    class="VisionHeading"
    :class="[
      { [`VisionHeading--size-${size || tag}`]: size || tag },
      { [`VisionHeading--align-${alignment}`]: alignment },
      { [`VisionHeading--font-style-${fontStyle}`]: fontStyle },
      { [`VisionHeading--text-transform-${textTransform}`]: textTransform },
      { [`VisionHeading--font-decoration-${fontDecoration}`]: fontDecoration },
      { [`VisionHeading--colour-style-${colourStyle}`]: colourStyle },
      { [`VisionHeading--font-weight-${fontWeight}`]: fontWeight },
    ]"
  >
    <slot />
  </component>
</template>

<script>
export default {
  name: 'VisionHeading',

  props: {
    tag: {
      type: String,
      default: 'h2',
      validator: (value) =>
        ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(value),
    },
    size: {
      type: String,
      default: '',
      validator: (value) =>
        ['', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(value),
    },
    alignment: {
      type: [Boolean, String],
      default: false,
      validator: (value) => [false, 'left', 'center', 'right'].includes(value),
    },
    fontStyle: {
      type: [Boolean, String],
      default: false,
      validator: (value) => [false, 'italic'].includes(value),
    },
    fontWeight: {
      type: [Boolean, String],
      default: false,
      validator: (value) => [false, 'light', 'mono'].includes(value),
    },
    textTransform: {
      type: [Boolean, String],
      default: false,
      validator: (value) =>
        [false, 'uppercase', 'lowercase', 'capitalize'].includes(value),
    },
    fontDecoration: {
      type: [Boolean, String],
      default: false,
      validator: (value) =>
        [false, 'underline', 'line-through'].includes(value),
    },
    colourStyle: {
      type: [Boolean, String],
      default: false,
      validator: (value) =>
        [false, 'colour', 'gradient', 'grey'].includes(value),
    },
  },
}
</script>

<style lang="postcss">
.VisionHeading {
  @apply font-sans font-semibold;
  --vision-heading-size: var(--vision-heading-size-override);
}

/* Sizes */

.VisionHeading--size-h1 {
  @apply leading-tight tracking-tight;
  font-size: var(--vision-heading-size, theme('fontSize.3xl'));

  @screen md {
    font-size: var(--vision-heading-size, theme('fontSize.4xl'));
  }
}

.VisionHeading--size-h2 {
  @apply leading-tight tracking-tight;
  font-size: var(--vision-heading-size, theme('fontSize.xl'));

  @screen md {
    font-size: var(--vision-heading-size, theme('fontSize.2xl'));
  }
}

.VisionHeading--size-h3 {
  @apply leading-tight tracking-tight;
  font-size: var(--vision-heading-size, theme('fontSize.base'));

  @screen md {
    font-size: var(--vision-heading-size, theme('fontSize.lg'));
  }
}

.VisionHeading--size-h4 {
  @apply leading-tight tracking-tight;
  font-size: var(--vision-heading-size, theme('fontSize.sm'));

  @screen md {
    font-size: var(--vision-heading-size, theme('fontSize.base'));
  }
}

.VisionHeading--size-h5 {
  @apply text-[12px] md:text-xs leading-tight;
  font-size: var(--vision-heading-size, 12px);

  @screen md {
    font-size: var(--vision-heading-size, theme('fontSize.xs'));
  }
}

.VisionHeading--size-h6 {
  @apply text-[12px] md:text-xxs leading-tight;
  font-size: var(--vision-heading-size, 12px);

  @screen md {
    font-size: var(--vision-heading-size, theme('fontSize.xxs'));
  }
}

/* Alignment */

.VisionHeading--align-left {
  @apply text-left;
}

.VisionHeading--align-center {
  @apply text-center;
}

.VisionHeading--align-right {
  @apply text-right;
}

/* Font Style */

.VisionHeading--font-style-italic {
  @apply italic;
}

.VisionHeading--font-style-mono {
  @apply font-fragment-mono;
}

/* Text Transform */

.VisionHeading--text-transform-uppercase {
  @apply uppercase;
}

.VisionHeading--text-transform-lowercase {
  @apply lowercase;
}

.VisionHeading--text-transform-capitalize {
  @apply capitalize;
}

/* Text Decoration */
.VisionHeading--font-decoration-underline {
  @apply underline;
}

.VisionHeading--font-decoration-line-through {
  @apply line-through;
}

/* Colour Style */
.VisionHeading--colour-style-gradient {
  @apply t-gradient;
}

.VisionHeading--colour-style-grey {
  @apply text-grey-500;
}

/* Weight */
.VisionHeading--font-weight-light {
  @apply font-light;
}
</style>
